import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { parseDbDate } from "@klubora/shared";
import type { BillingSnapshot } from "../../hooks/useBillingSnapshots";
import { billingStatusColor } from "../../lib/billingStatusColor";
import { colors, fontSize, fontWeight, radius, space } from "../../lib/tokens";
import { DataTable, type Column } from "../DataTable";
import { InvoiceActionMenu } from "./InvoiceActionMenu";

interface Tenant {
  id: string;
  slug: string;
  name: string;
}

interface InvoicesTableProps {
  snapshots: BillingSnapshot[];
  tenants: Tenant[];
  onAction: (action: string, snapshot: BillingSnapshot) => void;
  loading?: boolean;
}

export function InvoicesTable({ snapshots, tenants, onAction, loading }: InvoicesTableProps) {
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [tenantFilter, setTenantFilter] = useState<string[]>([]);

  const tenantById = useMemo(() => {
    const m = new Map<string, Tenant>();
    tenants.forEach((t) => m.set(t.id, t));
    return m;
  }, [tenants]);

  const filtered = useMemo(
    () =>
      snapshots.filter(
        (s) =>
          (statusFilter.length === 0 || statusFilter.includes(s.status)) &&
          (tenantFilter.length === 0 || tenantFilter.includes(s.tenant_id)),
      ),
    [snapshots, statusFilter, tenantFilter],
  );

  const fmtBRL = (v: number) =>
    v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  const fmtPeriod = (d: string) =>
    parseDbDate(d).toLocaleDateString("pt-BR", { month: "2-digit", year: "2-digit" });
  const fmtDueDate = (d: string) => parseDbDate(d).toLocaleDateString("pt-BR");
  // `due_date` e uma coluna `date`. A EXIBICAO ja pinava a meia-noite local a
  // mao; so a comparacao ficou com `new Date(d)` cru, que le a data como
  // meia-noite UTC — e marcava a fatura como vencida as 21h da vespera, em
  // UTC-3. Mesmo parser das duas formatacoes acima, agora com nome.
  const isLate = (s: BillingSnapshot) =>
    s.status === "overdue" ||
    (s.status === "sent" && s.due_date != null && parseDbDate(s.due_date) < new Date());

  const columns: Column<BillingSnapshot>[] = [
    {
      key: "tenant",
      header: "Tenant",
      render: (s) => {
        const t = tenantById.get(s.tenant_id);
        const late = isLate(s);
        const dot: React.CSSProperties = {
          display: "inline-block",
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: billingStatusColor("overdue").color,
          marginRight: space.sm,
          verticalAlign: "middle",
        };
        return (
          <span>
            {late && <span aria-label="Atrasada" style={dot} />}
            {t ? (
              <Link
                to={`/tenants/${t.slug}/billing`}
                style={{ color: colors.indigo, textDecoration: "none" }}
              >
                {t.name}
              </Link>
            ) : (
              "—"
            )}
          </span>
        );
      },
    },
    { key: "period", header: "Período", render: (s) => fmtPeriod(s.period_start) },
    {
      key: "total",
      header: "Total",
      align: "right",
      render: (s) => fmtBRL(Number(s.final_total)),
    },
    {
      key: "status",
      header: "Status",
      render: (s) => {
        const c = billingStatusColor(s.status);
        const badge: React.CSSProperties = {
          display: "inline-flex",
          alignItems: "center",
          padding: "3px 8px",
          borderRadius: radius.pill,
          fontSize: fontSize.xs,
          fontWeight: fontWeight.medium,
          lineHeight: 1.4,
          background: c.background,
          color: c.color,
          border: c.border,
        };
        return <span style={badge}>{c.label}</span>;
      },
    },
    {
      key: "due",
      header: "Vence",
      render: (s) => (s.due_date ? fmtDueDate(s.due_date) : "—"),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (s) => <InvoiceActionMenu snapshot={s} onAction={(a) => onAction(a, s)} />,
    },
  ];

  const wrap: React.CSSProperties = {
    background: colors.white,
    border: `1px solid ${colors.fog}`,
    borderRadius: radius.md,
    padding: space.lg,
  };
  const header: React.CSSProperties = {
    display: "flex",
    gap: space.sm,
    marginBottom: space.md,
    alignItems: "center",
  };
  const title: React.CSSProperties = {
    margin: 0,
    fontSize: fontSize.base,
    fontWeight: fontWeight.semibold,
    color: colors.ink,
    flex: 1,
  };
  const select: React.CSSProperties = {
    padding: `${space.xs + 2}px ${space.sm}px`,
    fontSize: fontSize.sm,
    border: `1px solid ${colors.fog}`,
    borderRadius: radius.sm,
    background: colors.white,
    color: colors.ink,
  };

  return (
    <div style={wrap}>
      <div style={header}>
        <h3 style={title}>Faturas ({filtered.length})</h3>
        <select
          value={statusFilter[0] ?? ""}
          onChange={(e) => setStatusFilter(e.target.value ? [e.target.value] : [])}
          style={select}
        >
          <option value="">Todos status</option>
          <option value="draft">Rascunho</option>
          <option value="sent">Enviada</option>
          <option value="paid">Paga</option>
          <option value="overdue">Atrasada</option>
          <option value="voided">Anulada</option>
          <option value="refunded">Reembolsada</option>
        </select>
        {tenants.length > 1 && (
          <select
            value={tenantFilter[0] ?? ""}
            onChange={(e) => setTenantFilter(e.target.value ? [e.target.value] : [])}
            style={select}
          >
            <option value="">Todos tenants</option>
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        )}
      </div>
      <DataTable columns={columns} rows={filtered} loading={loading} />
    </div>
  );
}
